import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthToken } from '../models/auth.models';
import { Utils } from './utils';
import { DataService } from './data.service';
import {
    buildAuthAppLogoutUrl,
    markAdminSignOutCookie,
} from '../utils/cross-app-logout.util';
import { buildAdminAuthEntryUrl } from '../utils/admin-auth-entry.util';
import { normalizeAuthAppUrl } from '../utils/auth-app-url.util';
import { getLaunchRequestedCompanyId, setLaunchRequestedCompanyId } from '../utils/admin-launch-context.util';

/** Response from POST /api/v1/auth/identity/session/refresh (Aya / Entra / Firebase). */
export interface SessionRefreshResponse {
    status?: string;
    token?: string;
    refreshToken?: string;
    email?: string;
    expiresIn?: number;
    expiresAt?: string;
    tokenType?: string;
}

@Injectable({ providedIn: 'root' })

export class AuthenticationService {
    private readonly sessionRefreshIntervalMs = 5 * 60 * 1000;
    private readonly tokenRefreshBufferMs = 5 * 60 * 1000;
    private sessionRefreshTimer: ReturnType<typeof setInterval> | null = null;
    private refreshSessionPromise: Promise<string | null> | null = null;

    constructor(
        private readonly _localStorageService: LocalStorageService,
        private readonly _dataService: DataService) {
    }

    /** Used by ErrorInterceptor on 401 — identity session refresh only. */
    getNewAccessToken(refreshToken: string): Observable<AuthToken> {
        return this.refreshSessionViaIdentityApi(refreshToken).pipe(
            map((res) => ({
                accessToken: (res.token || '').trim(),
                refreshToken: (res.refreshToken || refreshToken || '').trim(),
            })),
            catchError((err) => {
                console.warn('[admin-auth] identity session refresh failed', err);
                return throwError(() => err);
            })
        );
    }

    refreshSessionViaIdentityApi(
        refreshToken: string
    ): Observable<SessionRefreshResponse> {
        return this._dataService.post('api/v1/auth/identity/session/refresh', {
            refreshToken,
        });
    }

    getAuthTokenForCompany(userId: string, companyId: string): Observable<AuthToken> {
        setLaunchRequestedCompanyId(companyId);
        const accessToken = (this.authToken || '').trim();
        const refreshToken = (this.refreshToken || '').trim();
        if (!accessToken || this.isLegacyAccessToken(accessToken)) {
            return throwError(
                () => new Error('Identity session required to select a company.')
            );
        }
        return of({ accessToken, refreshToken });
    }

    /** True when the stored JWT already has an active organization. */
    hasOrganizationInToken(): boolean {
        const token = this.authToken;
        if (!token) {
            return false;
        }
        try {
            const payload = Utils.parseJwt(token);
            return !!(payload.companyId || payload.organizationId);
        } catch {
            return false;
        }
    }

    hideOrPinCompany(request: any): Observable<void> {
        return this._dataService.post('api/v1/users/pin-hide-company', request);
    }

    logoutAllApps(): Observable<void> {
        return this._dataService.post('api/v1/auth/logout', null);
    }

    deviceSessionCheck(): Observable<AuthToken> {
        if (!this._localStorageService.machineId) {
            return of(null);
        }
        return this._dataService.get(`api/v1/auth/check-session/${this._localStorageService.machineId}`);
    }

    get authToken(): string {
        if (!this._localStorageService.authorizationData) {
            return null;
        }
        return this._localStorageService.authorizationData.token;
    }

    get refreshToken(): string {
        if (!this._localStorageService.authorizationData) {
            return null;
        }
        return this._localStorageService.authorizationData.refreshToken;
    }

    logout(loggedOutFromAllApps: boolean = false): void {
        if (loggedOutFromAllApps) {
            this.logoutFromAllApps();
            return;
        }

        const authData = this._localStorageService.authorizationData;
        const hasSession =
            !!(authData?.token?.trim() || authData?.refreshToken?.trim());
        if (hasSession) {
            this.logoutFromAllApps();
            return;
        }

        this.clearSessionAndRedirectToLogin();
    }

    /** Clear local session and send user to wa-account-v2 login (`?app=admin`). */
    clearSessionAndRedirectToLogin(options?: { handoffFailed?: boolean }): void {
        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;
        markAdminSignOutCookie();
        this._localStorageService.clearAllClientStorage();
        this.redirectToLogin(0, options);
    }

    /**
     * Sign out via dev-auth, clear ask-aya-health in relay, land on aya-health-chat (dev-www / qa-www).
     */
    logoutFromAllApps(): void {
        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;

        const authData = this._localStorageService.authorizationData;
        const token = (authData?.token || '').trim();
        const refreshToken = (authData?.refreshToken || '').trim();

        markAdminSignOutCookie();
        this._localStorageService.clearAllClientStorage();

        this.logoutAllApps().subscribe({ error: () => undefined });

        window.location.href = this.buildCrossAppLogoutUrl(token, refreshToken);
    }

    private buildCrossAppLogoutUrl(token: string, refreshToken: string): string {
        // Pass final www landing only — wa-account-v2 /logout adds ask-aya relay (Aya-aligned).
        return buildAuthAppLogoutUrl({
            authAppUrl: this.getAuthEntryBaseUrl(),
            relayOrigins: [],
            finalReturnUrl: this.defaultLogoutLandingUrl(),
            everywhere: true,
            token: token || undefined,
            refreshToken: refreshToken || undefined,
        });
    }

    private defaultLogoutLandingUrl(): string {
        const www = (environment.ayaHealthChatAppUrl || '').trim().replace(/\/+$/, '');
        if (!www) {
            return this.buildAdminLoginUrl({ loggedOut: true });
        }
        const landing = www.startsWith('http://') || www.startsWith('https://')
            ? www
            : `https://${www}`;
        return `${landing}/?loggedOut=1`;
    }

    redirectToLogin(
        timeoutSecs: number = 1000,
        options?: { handoffFailed?: boolean }
    ) {
        setTimeout(() => {
            window.location.href = this.buildAdminLoginUrl(options);
        }, timeoutSecs);
    }

    buildAdminLoginUrl(options?: { loggedOut?: boolean; handoffFailed?: boolean }): string {
        return buildAdminAuthEntryUrl(
            {
                authAppUrl: environment.authAppUrl,
                loginUrl: environment.loginUrl,
            },
            options
        );
    }

    private getAuthEntryBaseUrl(): string {
        return normalizeAuthAppUrl(
            environment.authAppUrl?.trim() ||
            environment.loginUrl?.trim() ||
            ''
        );
    }

    get apiBaseUrl(): string {
        return environment.apiBaseUrl;
    }

    persistAuthData = (token: AuthToken): void => {
        if (this.isLegacyAccessToken(token.accessToken)) {
            console.warn('[admin-auth] Refusing to persist legacy company-scoped JWT.');
            return;
        }
        let jwtPayload: { exp?: number } = {};
        try {
            jwtPayload = Utils.parseJwt(token.accessToken);
        } catch {
            jwtPayload = {};
        }
        const authData = {
            token: token.accessToken,
            refreshToken: token.refreshToken,
            expiresAt: jwtPayload.exp
                ? Utils.getTokenExpireDate(jwtPayload.exp)
                : new Date(Date.now() + 60 * 60 * 1000),
        };

        this._localStorageService.authorizationData = authData;
        if (authData.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
    }

    get isTokenExpired(): boolean {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token) {
            return true;
        }
        if (authData.expiresAt) {
            return new Date(authData.expiresAt) <= new Date();
        }
        return this.isJwtExpired(authData.token);
    }

    get isTokenExpiringSoon(): boolean {
        if (this.isTokenExpired) {
            return true;
        }
        const authData = this._localStorageService.authorizationData;
        if (authData?.expiresAt) {
            return (
                new Date(authData.expiresAt).getTime() - Date.now() <=
                this.tokenRefreshBufferMs
            );
        }
        return this.isJwtExpiringSoon(authData?.token);
    }

    /** Returns a usable access token, refreshing proactively when close to expiry. */
    async getValidAccessToken(): Promise<string | null> {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token?.trim()) {
            return null;
        }
        if (!this.isTokenExpiringSoon) {
            return authData.token;
        }
        if (!authData.refreshToken?.trim()) {
            console.warn(
                '[admin-auth] Access token expiring soon but no refreshToken is stored — sign in again.'
            );
            return this.isTokenExpired ? null : authData.token;
        }
        const refreshed = await this.refreshSession('proactive');
        if (refreshed) {
            return refreshed;
        }
        return this.isTokenExpired ? null : authData.token;
    }

    isIdentityRefreshRequest(url: string): boolean {
        const normalized = (url || '').toLowerCase();
        return normalized.includes('/api/v1/auth/identity/session/refresh');
    }

    isLegacyRefreshRequest(url: string): boolean {
        const normalized = (url || '').toLowerCase();
        return normalized.includes('/api/v1/auth/refresh-token');
    }

    isLegacyRefreshToken(refreshToken: string | null | undefined): boolean {
        const payload = this.parseJwtPayload(String(refreshToken || '').trim());
        return payload?.['type'] === 'refresh_token';
    }

    isIdentityRefreshToken(refreshToken: string | null | undefined): boolean {
        const trimmed = String(refreshToken || '').trim();
        if (!trimmed || this.isLegacyRefreshToken(trimmed)) {
            return false;
        }
        if (/^AMf[-_]|^AEu/i.test(trimmed)) {
            return true;
        }
        return !this.parseJwtPayload(trimmed)?.['companyId'];
    }

    refreshSession(_trigger = 'on-demand'): Promise<string | null> {
        if (this.refreshSessionPromise) {
            return this.refreshSessionPromise;
        }
        this.refreshSessionPromise = this.performSessionRefresh()
            .catch((err) => {
                console.warn('[admin-auth] session refresh error', err);
                return null;
            })
            .finally(() => {
                this.refreshSessionPromise = null;
            });
        return this.refreshSessionPromise;
    }

    startPeriodicSessionRefresh(): void {
        if (typeof window === 'undefined' || this.sessionRefreshTimer) {
            return;
        }
        if (!this.refreshToken?.trim()) {
            return;
        }
        this.sessionRefreshTimer = setInterval(() => {
            if (!this.refreshToken?.trim()) {
                this.stopPeriodicSessionRefresh();
                return;
            }
            void this.refreshSessionIfNeeded();
        }, this.sessionRefreshIntervalMs);
    }

    stopPeriodicSessionRefresh(): void {
        if (this.sessionRefreshTimer) {
            clearInterval(this.sessionRefreshTimer);
            this.sessionRefreshTimer = null;
        }
    }

    private async refreshSessionIfNeeded(): Promise<void> {
        if (!this.refreshToken?.trim()) {
            return;
        }
        if (!this.isTokenExpiringSoon) {
            return;
        }
        await this.refreshSession('periodic');
    }

    private async performSessionRefresh(): Promise<string | null> {
        const authData = this._localStorageService.authorizationData;
        const storedRefresh = authData?.refreshToken?.trim();

        if (!storedRefresh) {
            return authData?.token && !this.isTokenExpired ? authData.token : null;
        }

        if (!this._localStorageService.machineId) {
            this._localStorageService.machineId = crypto.randomUUID();
        }

        if (
            this.isLegacyAccessToken(authData?.token) ||
            this.isLegacyRefreshToken(storedRefresh)
        ) {
            console.warn('[admin-auth] Legacy token detected — sign in again.');
            this._localStorageService.authorizationData = null;
            return null;
        }

        if (this.isIdentityRefreshToken(storedRefresh)) {
            try {
                const response = await firstValueFrom(
                    this.refreshSessionViaIdentityApi(storedRefresh)
                );
                const idToken = (response.token || '').trim();
                if (idToken) {
                    this.persistAuthDataFromSessionRefresh(response, storedRefresh);
                    return idToken;
                }
            } catch (err) {
                console.warn('[admin-auth] identity session refresh failed', err);
            }
        }

        return authData?.token && !this.isTokenExpired ? authData.token : null;
    }

    private isLegacyAccessToken(token: string | null | undefined): boolean {
        const payload = this.parseJwtPayload(String(token || '').trim());
        if (!payload) {
            return false;
        }
        return !!payload['companyId'] && payload['type'] !== 'refresh_token';
    }

    /** Firebase / Entra identity token (not legacy HS256 company_auth JWT). */
    isIdentityAccessToken(token?: string | null): boolean {
        const raw = String(token ?? this.authToken ?? '').trim();
        if (!raw || this.isLegacyAccessToken(raw)) {
            return false;
        }
        const payload = this.parseJwtPayload(raw);
        if (!payload) {
            return false;
        }
        const iss = String(payload['iss'] ?? '');
        if (iss.includes('securetoken.google.com')) {
            return true;
        }
        if (payload['oid']) {
            return true;
        }
        return !payload['companyId'];
    }

    /** Active workspace org for identity tokens (wa-account handoff / company picker). */
    resolveActiveOrganizationId(): string | null {
        return getLaunchRequestedCompanyId();
    }

    private persistAuthDataFromSessionRefresh(
        response: SessionRefreshResponse,
        fallbackRefresh: string
    ): void {
        const accessToken = (response.token || '').trim();
        if (!accessToken) {
            return;
        }

        let expiresAt: Date;
        if (response.expiresAt) {
            const parsed = Date.parse(response.expiresAt);
            expiresAt = Number.isFinite(parsed)
                ? new Date(parsed)
                : new Date(Date.now() + 60 * 60 * 1000);
        } else if (response.expiresIn) {
            expiresAt = new Date(Date.now() + response.expiresIn * 1000);
        } else {
            try {
                const exp = Utils.parseJwt(accessToken).exp;
                expiresAt = exp
                    ? Utils.getTokenExpireDate(exp)
                    : new Date(Date.now() + 60 * 60 * 1000);
            } catch {
                expiresAt = new Date(Date.now() + 60 * 60 * 1000);
            }
        }

        this._localStorageService.authorizationData = {
            token: accessToken,
            refreshToken: (response.refreshToken || fallbackRefresh).trim(),
            expiresAt,
        };
        if (this.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
    }

    private isJwtExpired(token: string): boolean {
        const payload = this.parseJwtPayload(token);
        const exp = Number(payload?.['exp']);
        if (!exp) {
            return false;
        }
        return exp * 1000 <= Date.now();
    }

    private isJwtExpiringSoon(token: string | undefined): boolean {
        if (!token) {
            return true;
        }
        const payload = this.parseJwtPayload(token);
        const exp = Number(payload?.['exp']);
        if (!exp) {
            return false;
        }
        return exp * 1000 - Date.now() <= this.tokenRefreshBufferMs;
    }

    private parseJwtPayload(token: string): Record<string, unknown> | null {
        try {
            return Utils.parseJwt(token) as Record<string, unknown>;
        } catch {
            return null;
        }
    }

    /**
     * Accept auth handoff from wa-account-v2 at /app-redirect.
     * Supports encrypted blob (?q=&d=) and readable Firebase/Entra tokens (?token=&refreshToken=).
     */
    hydrateAuthFromQueryParams(params: {
        token?: string | null;
        refreshToken?: string | null;
        q?: string | null;
        d?: string | null;
    }): boolean {
        const rawToken = String(params.token || '').trim();
        if (rawToken) {
            if (this.isLegacyAccessToken(rawToken)) {
                return false;
            }
            this.persistAuthData({
                accessToken: rawToken,
                refreshToken: String(params.refreshToken || '').trim(),
            });
            this.applyMachineIdFromQueryParam(params.d);
            if (this.refreshToken?.trim()) {
                this.startPeriodicSessionRefresh();
            }
            return this.hasReadableAuthSession();
        }

        const encrypted = this.decodeQueryParam(params.q);
        if (!encrypted) {
            return false;
        }

        this._localStorageService.authorizationDataEncrypted = encrypted;
        this.applyMachineIdFromQueryParam(params.d);
        const ok = this.hasReadableAuthSession();
        if (ok && this.isLegacyAccessToken(this.authToken)) {
            this._localStorageService.authorizationData = null;
            return false;
        }
        if (ok && !this.refreshToken?.trim()) {
            console.warn(
                '[admin-auth] Session handoff has access token but no refreshToken — periodic refresh and 401 recovery will not run until you sign in again.'
            );
        } else if (ok && this.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
        return ok;
    }

    /** After wa-account handoff, exchange Firebase/Entra refresh for a usable API session. */
    async ensureSessionReadyAfterHandoff(): Promise<boolean> {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token?.trim()) {
            return false;
        }

        const refresh = authData.refreshToken?.trim();
        if (
            refresh &&
            (this.isIdentityRefreshToken(refresh) ||
                this.isFirebaseIdentityToken(authData.token))
        ) {
            const token = await this.refreshSession('handoff');
            return !!token;
        }

        return this.hasReadableAuthSession();
    }

    private isFirebaseIdentityToken(token: string | null | undefined): boolean {
        const iss = String(this.parseJwtPayload(String(token || '').trim())?.['iss'] || '');
        return iss.includes('securetoken.google.com');
    }

    private decodeQueryParam(value: string | null | undefined): string {
        const raw = String(value || '').trim();
        if (!raw) {
            return '';
        }
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }

    private applyMachineIdFromQueryParam(machineIdParam: string | null | undefined): void {
        const decoded = this.decodeQueryParam(machineIdParam);
        if (decoded) {
            this._localStorageService.machineId = decoded;
            return;
        }
        if (!this._localStorageService.machineId) {
            this._localStorageService.machineId = crypto.randomUUID();
        }
    }

    private hasReadableAuthSession(): boolean {
        try {
            const authData = this._localStorageService.authorizationData;
            return !!authData?.token?.trim();
        } catch {
            this._localStorageService.authorizationData = null;
            return false;
        }
    }

    appendEncryptedAuthInfo(url: string, patientId: string = null): string {
        const queryParams = [
            `q=${encodeURIComponent(this._localStorageService.authorizationDataEncrypted)}`
        ];
        if (patientId) {
            queryParams.push(`id=${encodeURIComponent(patientId)}`);
        }
        if (this._localStorageService.machineId) {
            queryParams.push(`d=${encodeURIComponent(this._localStorageService.machineId)}`);
        }
        return `${url}/app-redirect?${queryParams.join('&')}`;
    }
}

export interface AuthData {
    token: string;
    refreshToken: string;
    expiresAt: Date;
}
